import { Suspense, lazy, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LangLink } from "@/components/LangLink";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useMagnetic } from "@/hooks/useMagnetic";
import { BRAND_NAV } from "@/lib/api";

const AtelierHeroCanvas = lazy(() => import("@/components/hero/AtelierHeroCanvas"));
const FALLBACK_IMG = "/kentucky/hero-rider.jpg";
const ease = [0.22, 1, 0.36, 1];

function webglAvailable() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl")));
  } catch {
    return false;
  }
}

const HeadlineLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const EditorialHero = ({ heroImage = null }) => {
  const { tx } = useI18n();
  const reduced = useReducedMotion();
  const [mount3D, setMount3D] = useState(false);
  const magneticCta = useMagnetic(0.3);

  const heroOverride = heroImage;

  useEffect(() => {
    if (!reduced && webglAvailable()) setMount3D(true);
  }, [reduced]);

  return (
    <section
      data-testid="home-hero"
      className="grain relative isolate overflow-hidden bg-[hsl(var(--brand-navy))] text-white"
    >
      {/* cinematic background */}
      <div className="absolute inset-0 -z-10">
        {heroOverride ? (
          // Admin-uploaded hero image takes priority over the WebGL/atelier scene.
          <motion.img
            src={heroOverride}
            alt=""
            aria-hidden="true"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: reduced ? 0 : 12, ease: "easeOut" }}
            className="h-full w-full object-cover opacity-55"
          />
        ) : mount3D ? (
          <Suspense fallback={<img src={FALLBACK_IMG} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-40" />}>
            <AtelierHeroCanvas />
          </Suspense>
        ) : (
          <motion.img
            src={FALLBACK_IMG}
            alt=""
            aria-hidden="true"
            initial={{ scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: reduced ? 0 : 12, ease: "easeOut" }}
            className="h-full w-full object-cover opacity-40"
          />
        )}
      </div>

      {/* navy legibility wash — vertical on mobile (text top, gallery below), horizontal on desktop */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-[hsl(var(--brand-navy))] via-[hsl(var(--brand-navy))]/55 to-[hsl(var(--brand-navy))]/15 md:hidden" />
      <div className="pointer-events-none absolute inset-0 -z-10 hidden bg-gradient-to-r from-[hsl(var(--brand-navy))] from-25% via-[hsl(var(--brand-navy))]/55 to-transparent md:block" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-32 bg-gradient-to-t from-[hsl(var(--brand-navy))] to-transparent" />

      <div className="container-site relative grid min-h-[86vh] items-start gap-10 pt-24 pb-16 md:items-center lg:grid-cols-12 lg:pt-28">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.32em] text-[hsl(var(--primary))]"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {tx("Performance. Elegance. Every Ride.")}
          </motion.p>

          <h1 className="mt-6 font-display text-[15vw] leading-[0.9] tracking-[-0.01em] sm:text-[84px] lg:text-[104px]">
            <HeadlineLine delay={0.12}>{tx("The Kentucky")}</HeadlineLine>
            <HeadlineLine delay={0.22}>{tx("Collection")}</HeadlineLine>
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, delay: 0.4, ease }}
            style={{ transformOrigin: "left" }}
            className="mt-7 h-px w-28 bg-[hsl(var(--primary))]"
          />

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-7 max-w-md text-[15px] leading-relaxed text-white/75"
          >
            {tx("Belgian-designed equestrian equipment, curated by Boomerang.ie, an official Kentucky Horsewear reseller. Held in our Ireland & EU warehouses and dispatched worldwide with express delivery.")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <LangLink
              ref={magneticCta}
              to="/brand/kentucky"
              data-testid="hero-shop-kentucky-button"
              className="group inline-flex items-center gap-3 rounded-full bg-[hsl(var(--primary))] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-[hsl(var(--primary-foreground))] shadow-[0_12px_40px_-12px_rgba(254,125,1,0.7)] transition-colors duration-200 hover:bg-[hsl(var(--primary))]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              {tx("Shop the Collection")}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </LangLink>
            <LangLink
              to="/size-guide"
              data-testid="hero-explore-guides-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-medium uppercase tracking-[0.14em] text-white transition-colors duration-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              {tx("Explore Guides")}
            </LangLink>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/55"
          >
            <span>{tx("Express Worldwide Shipping")}</span>
            <span className="h-1 w-1 rounded-full bg-[hsl(var(--primary))]" />
            <span>{tx("Ireland & EU Warehouses")}</span>
          </motion.div>
        </div>
      </div>

      {/* chapter pills */}
      <div className="relative border-t border-white/10">
        <div className="container-site flex flex-wrap items-center gap-x-7 gap-y-3 py-5">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">{tx("Maisons")}</span>
          {BRAND_NAV.map((b) => (
            <LangLink
              key={b.slug}
              to={`/brand/${b.slug}`}
              className="font-display text-xl text-white/80 transition-colors duration-200 hover:text-[hsl(var(--primary))] sm:text-2xl"
            >
              {b.name}
            </LangLink>
          ))}
        </div>
      </div>
    </section>
  );
};
